/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { When } from "react-if";
import * as yup from "yup";
import { Button, Label, Input, Sidebar, Description } from "@nordcloud/gnui";
import { Fieldset } from "~/components";

const schema = yup.object().shape({
  name: yup.string().required(),
  email: yup.string().email().required(),
});

type FormData = {
  id: string;
  name: string;
  email: string;
};

type Props = {
  isEditMode: boolean;
  isOpen: boolean;
  onClose: () => void;
  initialValues: FormData;
  onSubmit: SubmitHandler<FormData>;
};

export function EmployeeForm({
  isEditMode,
  isOpen,
  onClose,
  onSubmit,
  initialValues,
}: Props) {
  const {
    register,
    handleSubmit,
    reset,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const defaultValues = useMemo(
    () =>
      isEditMode
        ? initialValues
        : { id: initialValues.id, name: "", email: "" },
    // Primitive and unique `id` is used for shallow comparison to work
    [isEditMode, initialValues.id] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    reset();
  }, [defaultValues, reset]);

  return (
    <Sidebar
      title={isEditMode ? "Edit Contact Person" : "Create new Contact Person"}
      isOpen={isOpen}
      onClick={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <When condition={isEditMode}>
          <input type="hidden" {...register("id")} value={defaultValues.id} />
        </When>
        <Fieldset>
          <Label name="Name" htmlFor="name" />
          <Input
            id="name"
            type="text"
            {...register("name")}
            defaultValue={defaultValues.name}
            placeholder="Name"
            status={errors.name && "error"}
          />
          <When condition={errors.name}>
            <Description>Name is required.</Description>
          </When>
        </Fieldset>
        <Fieldset>
          <Label name="Email" htmlFor="email" />
          <Input
            id="email"
            type="text"
            {...register("email")}
            placeholder="Email"
            defaultValue={defaultValues.email}
            status={errors.email && "error"}
          />
          <When condition={errors.email}>
            <Description>Email is required.</Description>
          </When>
        </Fieldset>
        <Button type="submit">
          {isEditMode ? "Update Contact Person" : "Create New Contact Person"}
        </Button>
      </form>
    </Sidebar>
  );
}
